import { jwtDecode } from 'jwt-decode'

export type Decoded = {
  exp?: number
  [k: string]: unknown
}

/**
 * Extracts the expiration date (exp) from a JWT
 * @param token - JWT token
 * @returns Expiration timestamp in seconds (Unix epoch) or null if cannot be decoded
 */
export function getJwtExp(token: string | null): number | null {
  if (!token) return null
  try {
    const payload = jwtDecode<Decoded>(token)
    return typeof payload.exp === 'number' ? payload.exp : null
  } catch {
    return null
  }
}

/**
 * Verifies if a JWT is expired
 * @param token - JWT token
 * @param leewayMs - Time margin in milliseconds (default 10 seconds)
 * @returns true if the token is expired or about to expire within the margin
 */
export function isExpired(token: string | null, leewayMs = 10_000): boolean {
  const exp = getJwtExp(token)
  if (!exp) return false
  const expMs = exp * 1000
  return Date.now() >= expMs - leewayMs
}

/**
 * Extracts the user ID (sub claim) from a JWT
 * @param token - JWT token
 * @returns User ID string or null if cannot be decoded
 */
export function getCurrentUserId(token: string | null): string | null {
  if (!token) return null
  try {
    const payload = jwtDecode<Decoded & { sub?: string }>(token)
    return typeof payload.sub === 'string' ? payload.sub : null
  } catch {
    return null
  }
}

/**
 * Extracts the user role from a JWT
 * @param token - JWT token
 * @returns Role string or null if cannot be decoded
 */
export function getUserRole(token: string | null): string | null {
  if (!token) return null
  try {
    const payload = jwtDecode<Decoded & { role?: string }>(token)
    return typeof payload.role === 'string' ? payload.role : null
  } catch {
    return null
  }
}

/**
 * Extracts the user name from a JWT
 * @param token - JWT token
 * @returns Name string or null if cannot be decoded
 */
export function getUserName(token: string | null): string | null {
  if (!token) return null
  try {
    const payload = jwtDecode<Decoded & { name?: string }>(token)
    return typeof payload.name === 'string' ? payload.name : null
  } catch {
    return null
  }
}
